/**
 * SocialPostAsset Class
 *
 * Represents a social media post for a specific platform.
 * Each post is stored as a markdown file in the posts directory.
 */
import { join } from '../L1-infra/paths/paths.js'
import { TextAsset } from './TextAsset.js'
import type { AssetOptions } from './Asset.js'
import type { VideoAsset } from './VideoAsset.js'
import type { Platform } from '../L0-pure/types/index.js'

/**
 * A social media post asset for a specific platform.
 *
 * Posts are generated by AI agents and stored as markdown files.
 * This class provides the Asset interface for loading them.
 */
export class SocialPostAsset extends TextAsset {
  /** The video this post is for (main video, short, or medium clip) */
  readonly parent: VideoAsset

  /** Target platform (TikTok, YouTube, Instagram, LinkedIn, X) */
  readonly platform: Platform

  /** Path to the post markdown file */
  readonly filePath: string

  /**
   * Create a new social post asset.
   *
   * @param parent - The video this post is for
   * @param platform - Target social media platform
   * @param postsDir - Directory containing platform post files
   */
  constructor(parent: VideoAsset, platform: Platform, postsDir: string) {
    super()
    this.parent = parent
    this.platform = platform
    this.filePath = join(postsDir, `${platform.toLowerCase()}.md`)
  }

  /**
   * Get the post content for this platform.
   *
   * Loads from disk if exists, throws if not found.
   *
   * @param opts - Options controlling generation behavior
   * @returns The post content as markdown
   */
  async getResult(opts?: AssetOptions): Promise<string> {
    // Check memory cache first
    if (!opts?.force && this._result !== undefined) {
      return this._result
    }

    // Load from disk
    const content = await this.loadFromDisk()
    if (content === null) {
      throw new Error(
        `Social post not found for ${this.platform} at ${this.filePath}. ` +
          `Run the social media stage first.`,
      )
    }

    this._result = content
    return content
  }
}
